import { useState } from 'react';

import clsx from 'clsx';

import { TranslationService } from '@/utils/services/TranslationService';

// Hooks
import { CalendarProvider } from '@/utils/hooks/scheduler/useCalendar';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

// Lib
import {
    calendarBttpLocalStorageKey,
    calendarFoLocalStorageKey,
    calendarSmcLocalStorageKey,
    calendarTimeSlotsLocalStorageKey,
    getCalendarFilterLocalStorage
} from '@/utils/localStorage/scheduler/calendarFiltersLocalStorage';
import { getCalendarMediaLibraryLocalStorage } from '@/utils/localStorage/scheduler/calendarMediaLibraryLocalStorage';
import { getCalendarViewLocalStorage } from '@/utils/localStorage/scheduler/calendarViewLocalStorage';
// Utils
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

import { SCHEDULER } from '@/lib/routes/privateRoutes';

// Components
import { WrapperSwitchLayout } from '@/components/layouts/wrappers/WrapperSwitchLayout';
import CalendarView from '@/components/pages/scheduler/calendar/CalendarView';
import MobileViewAccessModal from '@/components/pages/scheduler/restrictAccessModals/MobileViewAccessModal';
import SchedulerAccessRights, {
    ModalRestrictionPage
} from '@/components/pages/scheduler/SchedulerAccessRights';
import SchedulerHeader from '@/components/pages/scheduler/SchedulerHeader/SchedulerHeader';
import { PageContentWrapper } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper';

import SchedulerNewMobileAppModal from '@/pages/publishing/scheduler/modal/SchedulerNewMobileAppModal';

const SchedulerContent = ({ hasSchedulerPermission, shouldBlockView }) => {
    const [needReloadScheduledPosts, setNeedReloadScheduledPosts] = useState(false);
    const isGroup = useIsGroup();
    const { activeGroup } = useActiveGroup();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });
    const { spGroups } = useSPGroups({ revalidateIfStale: false });

    return (
        <>
            <SchedulerHeader
                isGroup={isGroup}
                activeGroup={activeGroup}
                activeSocialProfile={activeSocialProfile}
                hasSchedulerPermission={hasSchedulerPermission}
                setNeedReloadScheduledPosts={setNeedReloadScheduledPosts}
            />
            <SchedulerNewMobileAppModal />
            <PageContentWrapper>
                <div
                    className={clsx('relative z-0 flex flex-1 flex-col rounded-t-md', {
                        'h-full max-h-screen overflow-hidden': shouldBlockView
                    })}>
                    <CalendarProvider
                        defaultValues={{
                            title: null,
                            view: getCalendarViewLocalStorage(),
                            filter_post_status: 'all',
                            events: {
                                smc: {
                                    active: getCalendarFilterLocalStorage(
                                        calendarSmcLocalStorageKey
                                    )
                                },
                                followers_online: {
                                    active: getCalendarFilterLocalStorage(calendarFoLocalStorageKey)
                                },
                                best_time_to_post: {
                                    active: getCalendarFilterLocalStorage(
                                        calendarBttpLocalStorageKey
                                    )
                                },
                                time_slots: {
                                    active: getCalendarFilterLocalStorage(
                                        calendarTimeSlotsLocalStorageKey
                                    )
                                }
                            },
                            openMediaLibrary: {
                                active: getCalendarMediaLibraryLocalStorage()
                            }
                        }}>
                        <CalendarView
                            isGroup={isGroup}
                            activeGroup={activeGroup}
                            activeSocialProfile={activeSocialProfile}
                            activatedSocialProfiles={activatedSocialProfiles}
                            spGroups={spGroups}
                            blockView={shouldBlockView}
                            needReloadScheduledPosts={needReloadScheduledPosts}
                            setNeedReloadScheduledPosts={setNeedReloadScheduledPosts}
                            hasSchedulerPermission={hasSchedulerPermission}
                        />
                    </CalendarProvider>
                    {!isGroup ? (
                        <ModalRestrictionPage
                            activeSocialProfile={activeSocialProfile}
                            hasSchedulerPermission={hasSchedulerPermission}
                        />
                    ) : null}
                    <MobileViewAccessModal />
                </div>
            </PageContentWrapper>
        </>
    );
};

const SchedulerIntermediateContent = () => {
    const isGroup = useIsGroup();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeMultiSelectionProfiles } = useProfilesStore((state) => state);

    if (
        isGroup ||
        (activeMultiSelectionProfiles?.socialProfileIds?.length && !activeSocialProfile)
    ) {
        return <SchedulerContent hasSchedulerPermission={true} shouldBlockView={false} />;
    }

    return (
        <SchedulerAccessRights className="relative h-full">
            {({ hasSchedulerPermission, shouldBlockView }) => (
                <SchedulerContent
                    hasSchedulerPermission={hasSchedulerPermission}
                    shouldBlockView={shouldBlockView}
                />
            )}
        </SchedulerAccessRights>
    );
};

const Scheduler = () => {
    return (
        <WrapperSwitchLayout
            noPadding
            className="scheduler-wrapper-element px-5 pt-5 sm:px-8 sm:pt-8"
            bodyWrapperClass="scheduler-wrapper-element">
            <SchedulerIntermediateContent />
        </WrapperSwitchLayout>
    );
};

export const getStaticProps = TranslationService.getTranslationMessagesFromRoute(SCHEDULER);

export default Scheduler;
